@if (filesService.available()) {
  @let files = filesService.files();
  @let count = filesService.count();
  <span class="room-files-trigger">
    <andor-square-icon-btn
      [variant]="menu.isOpen() ? 'active' : 'default'"
      [tooltip]="menu.isOpen() ? '' : ('Files' | translate)"
      [tooltipPosition]="tooltipPosition()"
      [ariaLabel]="'Files' | translate"
      [andorContextMenuTriggerFor]="menu">
      <fa-duotone-icon [icon]="faPaperclip"></fa-duotone-icon>
    </andor-square-icon-btn>
    @if (count > 0) {
      <span class="room-files-count" aria-hidden="true">
        {{ count > 99 ? '99+' : count }}
      </span>
    }
  </span>

  <andor-context-menu
    #menu
    mode="styled"
    [xPosition]="menuXPosition()"
    [yPosition]="menuYPosition()"
    [overlapTrigger]="false"
    (opened)="onMenuOpened()">
    @let usage = filesService.usage();
    @let draft = filesService.draft();
    @let uploads = filesService.uploads();
    <div class="room-files">
      <div class="room-files__header">
        <span class="room-files__title">{{ 'Files' | translate }}</span>
        @if (usage) {
          <span class="room-files__usage">
            {{
              '[used] of [total] used'
                | translate
                  : {
                      used: formatBytes(usage.usedBytes, 1),
                      total: formatBytes(usage.maxTotalBytes, 0),
                    }
            }}
          </span>
        }
      </div>

      @if (filesService.canShare()) {
        @if (draft) {
          <div class="room-files__draft">
            <ul class="room-files__list room-files__list--draft">
              @for (file of draft.files; track $index) {
                @let problem = filesService.draftProblem(file);
                <li class="room-files__item" [class.room-files__item--invalid]="problem">
                  <fa-duotone-icon
                    class="room-files__type"
                    [icon]="fileTypeIcon(file.name, file.type)"></fa-duotone-icon>
                  <div class="room-files__meta">
                    <span class="room-files__name" [title]="file.name">{{ file.name }}</span>
                    <span class="room-files__sub">{{ problem || formatBytes(file.size, 1) }}</span>
                  </div>
                  <div class="room-files__actions">
                    <andor-square-icon-btn
                      [ariaLabel]="'Remove' | translate"
                      (clicked)="filesService.removeFromDraft(file)">
                      <fa-duotone-icon [icon]="faXmark"></fa-duotone-icon>
                    </andor-square-icon-btn>
                  </div>
                </li>
              }
            </ul>

            <div class="room-files__field">
              <span class="room-files__label" id="room-files-retention">
                {{ 'Available for' | translate }}
              </span>
              <div class="room-files__segments" role="radiogroup" aria-labelledby="room-files-retention">
                @for (option of retentionOptions; track option.value) {
                  <button
                    type="button"
                    role="radio"
                    class="room-files__segment"
                    [class.room-files__segment--active]="draft.retention === option.value"
                    [attr.aria-checked]="draft.retention === option.value"
                    (click)="filesService.updateDraft({ retention: option.value })">
                    {{ option.label | translate }}
                  </button>
                }
              </div>
              @if (draft.retention === 'room') {
                <span class="room-files__hint">{{ retentionHint() | translate }}</span>
              }
            </div>

            <div class="room-files__field">
              <span class="room-files__label" id="room-files-protection">
                {{ 'Encryption' | translate }}
              </span>
              <div class="room-files__segments" role="radiogroup" aria-labelledby="room-files-protection">
                @for (option of protectionOptions(); track option.value) {
                  <button
                    type="button"
                    role="radio"
                    class="room-files__segment"
                    [class.room-files__segment--active]="draft.protection === option.value"
                    [attr.aria-checked]="draft.protection === option.value"
                    (click)="filesService.updateDraft({ protection: option.value })">
                    {{ option.label | translate }}
                  </button>
                }
              </div>
              @if (draft.protection === 'custom') {
                <div class="room-files__password">
                  <input
                    class="room-files__input"
                    [type]="showPassword() ? 'text' : 'password'"
                    autocomplete="new-password"
                    [placeholder]="'Password' | translate"
                    [attr.aria-label]="'Password' | translate"
                    [value]="draft.password"
                    (input)="setPassword($event)"
                    (keydown.enter)="upload()" />
                  <andor-square-icon-btn
                    [ariaLabel]="(showPassword() ? 'Hide password' : 'Show password') | translate"
                    (clicked)="showPassword.set(!showPassword())">
                    <fa-duotone-icon [icon]="showPassword() ? faEyeSlash : faEye"></fa-duotone-icon>
                  </andor-square-icon-btn>
                </div>
              }
              <span class="room-files__hint">{{ protectionHint() | translate }}</span>
            </div>

            <div class="room-files__draft-actions">
              <andor-button density="compact" (click)="filesService.discardDraft()">
                {{ 'Cancel' | translate }}
              </andor-button>
              <andor-button
                density="compact"
                type="gradient"
                [disabled]="!canUpload()"
                (click)="upload()">
                {{ 'Upload' | translate }}
              </andor-button>
            </div>
          </div>
        } @else {
          <andor-file-drop-zone
            class="room-files__drop"
            multiple
            [icon]="faFileArrowUp"
            [heading]="'Upload files' | translate"
            [hint]="dropHint()"
            [ariaLabel]="'Upload files' | translate"
            (filesSelected)="filesService.select($event)" />
        }
      }

      @if (uploads.length > 0 || files.length > 0) {
        <ul class="room-files__list">
          @for (upload of uploads; track upload.id) {
            <li
              class="room-files__item room-files__item--uploading"
              [class.room-files__item--failed]="upload.state === 'failed'">
              <fa-duotone-icon
                class="room-files__type"
                [icon]="fileTypeIcon(upload.name, upload.mime)"></fa-duotone-icon>
              <div class="room-files__meta">
                <span class="room-files__name" [title]="upload.name">
                  {{ upload.name }}
                  @if (upload.encrypted) {
                    <fa-duotone-icon class="room-files__lock" [icon]="faLock"></fa-duotone-icon>
                  }
                </span>
                @if (upload.state === 'failed') {
                  <span class="room-files__sub room-files__sub--error">{{ upload.error }}</span>
                } @else {
                  <span class="room-files__sub">
                    <fa-duotone-icon class="room-files__spinner" [icon]="faSpinnerThird"></fa-duotone-icon>
                    @switch (upload.state) {
                      @case ('preparing') {
                        {{ (upload.encrypted ? 'Encrypting…' : 'Preparing…') | translate }}
                      }
                      @case ('completing') {
                        {{ 'Finishing…' | translate }}
                      }
                      @case ('waiting') {
                        {{ 'Waiting to retry' | translate }}…
                      }
                      @default {
                        {{ 'Uploading…' | translate }} {{ (upload.progress * 100).toFixed(0) }}%
                      }
                    }
                  </span>
                  <span class="room-files__progress" aria-hidden="true">
                    <span [style.width.%]="upload.progress * 100"></span>
                  </span>
                }
              </div>
              <div class="room-files__actions">
                <andor-square-icon-btn
                  [ariaLabel]="(upload.state === 'failed' ? 'Remove' : 'Cancel upload') | translate"
                  (clicked)="
                    upload.state === 'failed'
                      ? filesService.dismissUpload(upload.id)
                      : filesService.cancelUpload(upload.id)
                  ">
                  <fa-duotone-icon [icon]="faXmark"></fa-duotone-icon>
                </andor-square-icon-btn>
              </div>
            </li>
          }
          @for (file of files; track file.id) {
            @let busy = filesService.downloading().has(file.id);
            <li class="room-files__item">
              <fa-duotone-icon
                class="room-files__type"
                [icon]="fileTypeIcon(file.name, file.mime)"></fa-duotone-icon>
              <div class="room-files__meta">
                <span class="room-files__name" [title]="file.name">
                  {{ file.name }}
                  @if (file.encryption) {
                    <fa-duotone-icon
                      class="room-files__lock"
                      [icon]="faLock"
                      [title]="'Encrypted' | translate"></fa-duotone-icon>
                  }
                </span>
                <span class="room-files__sub">
                  {{ formatBytes(file.size, 1) }} · {{ uploaderLabel(file) }} ·
                  {{ file.uploadedAt | localizedDate: 'shortTime' }}
                  @if (file.expiresAt !== null) {
                    · {{ expiryLabel(file) }}
                  }
                </span>
                @if (downloads.isUnlocking(file)) {
                  <div class="room-files__password room-files__password--unlock">
                    <input
                      class="room-files__input"
                      type="password"
                      autocomplete="off"
                      [placeholder]="'File password' | translate"
                      [attr.aria-label]="'File password' | translate"
                      [class.room-files__input--error]="downloads.unlocking()?.wrong"
                      [value]="downloads.password()"
                      (input)="downloads.setPassword($event)"
                      (keydown.enter)="downloads.submit(file)"
                      (keydown.escape)="downloads.cancel()" />
                    <andor-button density="compact" (click)="downloads.submit(file)">
                      {{ 'Open' | translate }}
                    </andor-button>
                  </div>
                  @if (downloads.unlocking()?.wrong) {
                    <span class="room-files__sub room-files__sub--error">
                      {{ 'Wrong password.' | translate }}
                    </span>
                  }
                }
              </div>
              <div class="room-files__actions">
                <andor-square-icon-btn
                  [disabled]="busy"
                  [ariaLabel]="'Download' | translate"
                  (clicked)="downloads.download(file)">
                  <fa-duotone-icon
                    [class.room-files__spinner]="busy"
                    [icon]="busy ? faSpinnerThird : faDownload"></fa-duotone-icon>
                </andor-square-icon-btn>
                @if (filesService.canShare()) {
                  @let confirming = confirmingDelete() === file.id;
                  <andor-square-icon-btn
                    variant="danger"
                    [tooltip]="confirming ? ('Click again to delete for everyone' | translate) : ''"
                    [ariaLabel]="(confirming ? 'Confirm deletion' : 'Delete') | translate"
                    (clicked)="onDelete(file)">
                    <fa-duotone-icon [icon]="confirming ? faCheck : faTrashCan"></fa-duotone-icon>
                  </andor-square-icon-btn>
                }
              </div>
            </li>
          }
        </ul>
      } @else if (!draft) {
        <p class="room-files__empty">{{ 'No files shared in this room yet.' | translate }}</p>
      }
    </div>
  </andor-context-menu>
}
